import {useCallback} from "react";
import {getStatusForCard} from "../../lib/card-status-utils";
import {waitForResultPromise} from "../../lib/wait-for-result";
import useMutation from "../../lib/hooks/useMutation";
import {api} from "../../lib/api";

export const getEffort = (card: any) => {
  if (
    card.childCards.length === 0 ||
    (card.childCards.length === 1 && !card.childCards[0].$meta.isLoaded)
  ) {
    return card.effort;
  } else {
    const childEffort = getChildCardEffort(getChildCards(card));

    return childEffort === null && card.effort === null
      ? null
      : (childEffort || 0) + (card.effort || 0);
  }
};

export const getChildCardEffort = (childCards: any[]) => {
  let hasNonNullEffort = false;
  let sum = 0;
  childCards.forEach((c) => {
    if ((c.status !== "done" && c.visibility !== "default") || c.isDoc) return;
    const eff = c.$meta.get("effort", null);
    if (eff !== null) {
      hasNonNullEffort = true;
      sum += eff;
    }
  });
  return hasNonNullEffort ? sum : null;
};

export const getChildCards = (card: any): any[] => {
  const {childCards} = card;
  if (!childCards || (childCards.length === 1 && !childCards[0].$meta.isLoaded)) return [];
  return process.env.REACT_APP_MODE === "open"
    ? childCards.filter((c: any) => c.isPublic)
    : childCards;
};

export const hasChildCards = (card: any): boolean => {
  const childCards = getChildCards(card);
  if (childCards.length === 0) return false;
  return childCards[0].$meta.isLoaded;
};

export const getParentCard = (card: any): any | null => {
  if (card.parentCard === null || card.parentCard.$meta.isDeleted()) return null;
  if (process.env.REACT_APP_MODE === "open") {
    return card.parentCard.$meta.get("isPublic", false) ? card.parentCard : null;
  }
  return card.parentCard;
};

export const getChildCardInfo = (card: any) => {
  const childCards = getChildCards(card);
  if (childCards.length === 0) return null;

  const childEffort = getChildCardEffort(childCards);

  const info: {[key: string]: number[]} & {effort: number} = {
    started: [],
    blocked: [],
    review: [],
    done: [],
    rest: [],
    effort:
      childEffort === null && card.effort === null ? null : (childEffort || 0) + (card.effort || 0),
  };

  childCards.forEach((c: any) => {
    const status = getStatusForCard(c) as "started" | "doc";
    if (status in info) {
      info[status].push(c.effort);
    } else if (card.isDoc && status === "doc") {
      info.done.push(c.effort);
    } else if ((c.visibility === "default" || c.status === "done") && status !== "doc") {
      info.rest.push(c.effort);
    }
  });

  return info;
};

export const areValidChildren = (cards: any[]) => {
  if (cards.some((c) => !c.deck)) {
    return {isValid: false, msg: "Can't add private cards as sub cards."};
  } else if (cards.some((c) => c.childCards.length > 0)) {
    return {isValid: false, msg: "Can't add Hero cards as sub cards."};
  } else if (cards.some((c) => c.parentCard && c.parentCard.$meta.isDeleted())) {
    return {isValid: false, msg: "Card is already part of inaccessible Hero card."};
  }
  return {isValid: true};
};

export const checkDropCards = (
  parentCard: any,
  cardInfos: {cardId: string; getCard: () => any}[],
  {mayDropExistingChildren}: {mayDropExistingChildren?: boolean} = {}
) => {
  if (!parentCard || parentCard.$meta.isDeleted()) {
    return {canDrop: false, msg: "You don't have access to the parent card"};
  }
  if (!parentCard.deck) return {canDrop: false, msg: "Can't turn private card into a Hero card."};
  if (parentCard.isDoc && !hasChildCards(parentCard)) {
    return {canDrop: false, msg: "Can't turn Doc cards into Hero cards."};
  }
  const allCardsAreMyChildren = cardInfos.every((i) =>
    parentCard.childCards.find((c: any) => c.id === i.cardId)
  );
  if (!mayDropExistingChildren && allCardsAreMyChildren) {
    return {
      canDrop: false,
      msg: "Can't drop: already sub card of this card.",
      isAlreadyChild: true,
    };
  } else if (cardInfos.some((i) => i.cardId === parentCard.id)) {
    return {canDrop: false, msg: "Can't drop a card onto itself."};
  } else {
    const cards = cardInfos.map((i) => i.getCard());
    const {isValid, msg} = areValidChildren(cards);
    return {canDrop: isValid, msg};
  }
};

export const useAddCardToParent = () => {
  const [update] = useMutation("cards", "update");
  return useCallback(
    (opts: {parentCardId: any; childCardIds: string[]}) =>
      waitForResultPromise(() => {
        const parentCard = api.getModel({modelName: "card", id: opts.parentCardId} as any);
        return {
          existingIds: parentCard.$meta.get("childCards", [], {forceRelIds: true}) || [],
        };
      }).then(({existingIds}) => {
        const set = new Set(existingIds);
        return update({
          id: opts.parentCardId,
          childCards: [...existingIds, ...opts.childCardIds.filter((id) => !set.has(id))],
        });
      }),
    [update]
  );
};

export const deckHasDefaultCard = (deck: any) => {
  if (!deck.defaultCard) return false;
  const {
    effort = null,
    priority = null,
    assigneeId = null,
    masterTags = [],
    content,
  } = deck.defaultCard;
  return (
    effort !== null ||
    priority !== null ||
    assigneeId !== null ||
    (masterTags || []).length > 0 ||
    content
  );
};

export type ChildCardInfo = {
  groups: {
    id: number;
    label: string | null;
    description: string | null;
    cards: string[];
  }[];
};

type Entry = {id: string; card: any | null};

export type ChildCardInfoGroup = {
  id: number;
  label: string | null;
  description: string | null;
  entries: Entry[];
  isDefaultGroup: boolean;
};

export const getChildCardEntries = (card: any, opts: {includeInaccessible: boolean}) => {
  const showAll = process.env.REACT_APP_MODE !== "open" && opts.includeInaccessible;
  const childCardEntries: {id: string; card: any | null}[] = showAll
    ? card.childCards.map((c: any) => ({id: c.id, card: c}))
    : (card.$meta.get("childCards", [], {forceRelIds: true}) || []).map((id: string) => ({
        id,
        card: api.getModel({id, modelName: "card"} as any),
      }));
  return process.env.REACT_APP_MODE === "open"
    ? childCardEntries.filter((c) => c.card.isPublic)
    : childCardEntries;
};
